import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthLayout from "@/pages/AuthLayout";
import GoogleButton from "@/pages/GoogleButton";

export default function Login() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.demo) {
      setEmail("demo@fitpulse.app");
      setPassword("demo1234");
    }
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        toast.success(t("sendResetLink"));
        setForgot(false);
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        navigate(data.user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={forgot ? t("resetPassword") : t("welcomeBack")} subtitle={forgot ? t("email") : ""}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">{t("email")}</Label>
          <Input id="email" data-testid="login-email" type="email" required value={email}
            autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
            onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" placeholder="you@email.com" />
        </div>
        {!forgot && (
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">{t("password")}</Label>
              <button type="button" onClick={() => setForgot(true)} className="text-xs text-primary hover:underline" data-testid="forgot-link">
                {t("forgotPassword")}
              </button>
            </div>
            <div className="relative mt-1.5">
              <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} required value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl pr-11" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="toggle-password">
                {showPw ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
              </button>
            </div>
          </div>
        )}
        <Button type="submit" size="lg" className="h-12 w-full rounded-xl text-base font-semibold" disabled={loading} data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (forgot ? t("sendResetLink") : t("login"))}
        </Button>
        {forgot && (
          <button type="button" onClick={() => setForgot(false)} className="w-full text-center text-xs text-muted-foreground hover:underline">
            {t("back")}
          </button>
        )}
      </form>
      {!forgot && (
        <>
          <Divider />
          <GoogleButton />
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t("noAccount")} <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="to-register">{t("register")}</Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
}

function Divider() {
  return (
    <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
      <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
    </div>
  );
}
